
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import PageLayout from '@/components/PageLayout';
import { useCar } from '@/contexts/CarContext';
import { useCarSelectionContext } from '@/contexts/CarSelectionContext';
import { useSettings } from '@/contexts/SettingsContext';
import ServiceRecords from '@/components/ServiceRecords';
import { BarChart2, AlertTriangle, Wrench, Calendar, Filter, Car as CarIcon, ChevronDown } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const ReportsPage = () => {
  const { cars } = useCar();
  const { currentCar, currentCarId, selectedCarId, selectCar } = useCarSelectionContext();
  const { t } = useSettings();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | 'issues' | 'repairs'>('all');
  
  const selectedCar = currentCar;
  
  const hasIssues = selectedCar?.issues && selectedCar.issues.length > 0;
  const hasRepairs = selectedCar?.repairs && selectedCar.repairs.length > 0;
  const hasData = hasIssues || hasRepairs;
  
  const timelineItems = React.useMemo(() => {
    if (!selectedCar) return [];
    
    let items = [];
    
    if (filter === 'all' || filter === 'issues') {
      const issues = selectedCar.issues?.map(issue => ({
        id: issue.id,
        type: 'issue',
        title: issue.title,
        description: issue.description,
        date: issue.date,
        status: issue.status,
        data: issue
      })) || [];
      
      items = [...items, ...issues];
    }
    
    if (filter === 'all' || filter === 'repairs') {
      const repairs = selectedCar.repairs?.map(repair => ({
        id: repair.id,
        type: 'repair',
        title: repair.title,
        description: repair.description || '',
        date: repair.date,
        cost: repair.cost,
        garageName: repair.garageName,
        data: repair
      })) || [];
      
      items = [...items, ...repairs];
    }
    
    return items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [selectedCar, filter]);
  
  const groupedItems = React.useMemo(() => {
    if (!timelineItems.length) return {};
    
    return timelineItems.reduce((acc, item) => {
      const date = new Date(item.date);
      const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`;
      
      if (!acc[monthYear]) {
        acc[monthYear] = [];
      }
      
      acc[monthYear].push(item);
      return acc;
    }, {} as Record<string, typeof timelineItems>);
  }, [timelineItems]);
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const getStatusLabel = (status: string) => {
    switch(status) {
      case 'resolved': return 'Resolved';
      case 'partially_resolved': return 'Partially Resolved';
      case 'processing': return 'In Progress';
      case 'new': default: return 'New';
    }
  };
  
  const getStatusClass = (status: string) => {
    switch(status) {
      case 'resolved': return 'bg-lime/20 text-black';
      case 'partially_resolved': return 'bg-yellow-400/20 text-black';
      case 'processing': return 'bg-yellow-400/20 text-black';
      case 'new': default: return 'bg-red-500/20 text-red-700';
    }
  };
  
  const handleFilterChange = (value: 'all' | 'issues' | 'repairs') => {
    setFilter(value);
  };

  const handleCarSelect = (carId: string) => {
    selectCar(carId);
  };
  
  return (
    <PageLayout title={t('reports.title')}>
      {cars.length === 0 ? (
        <div className="text-center py-6">
          <p className="mb-2">You need to add a car to view reports</p>
          <button 
            onClick={() => navigate('/cars/add')}
            className="btn-primary"
          >
            Add Your First Car
          </button>
        </div>
      ) : !hasData ? (
        <div className="text-center py-6">
          <BarChart2 size={48} className="mx-auto text-gray-300 mb-2" />
          <p className="text-gray-500 mb-2">{t('reports.noReports')}</p>
          <p className="text-sm text-gray-400 mb-3">
            Log your first car issue or repair to generate reports
          </p>
          <div className="flex flex-col gap-2 items-center">
            <button 
              onClick={() => navigate('/log-symptom')}
              className="btn-primary"
            >
              Log an Issue
            </button>
            <button 
              onClick={() => navigate(`/log-repair/${selectedCar?.id}`)}
              className="btn-outline"
            >
              Log a Repair
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Car selection dropdown */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold mb-2">Car Overview</h2>
                <p className="text-sm text-gray-500 mb-1">{selectedCar?.brand} {selectedCar?.model}</p>
                <p className="font-medium">{selectedCar?.year} • {selectedCar?.licensePlate || 'No plate'}</p>
              </div>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2">
                    <CarIcon size={16} />
                    <span>Switch Car</span>
                    <ChevronDown size={16} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white">
                  <DropdownMenuLabel>Select a car</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {cars.map((car) => (
                    <DropdownMenuItem 
                      key={car.id}
                      onClick={() => handleCarSelect(car.id)}
                      className={car.id === selectedCarId ? "bg-lime/20" : ""}
                    >
                      <span className="font-medium">
                        {car.nickname || `${car.brand} ${car.model}`}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-2 shadow-sm">
            <ToggleGroup 
              type="single" 
              value={filter} 
              onValueChange={(value) => value && handleFilterChange(value as 'all' | 'issues' | 'repairs')}
              className="flex w-full justify-between max-w-full overflow-x-auto no-scrollbar"
            >
              <ToggleGroupItem value="all" className="flex-1 text-xs sm:text-sm">
                <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="whitespace-nowrap">All</span>
              </ToggleGroupItem>
              <ToggleGroupItem value="issues" className="flex-1 text-xs sm:text-sm">
                <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="whitespace-nowrap">Issues</span>
              </ToggleGroupItem>
              <ToggleGroupItem value="repairs" className="flex-1 text-xs sm:text-sm">
                <Wrench className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="whitespace-nowrap">Repairs</span>
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm">
            <h2 className="text-xl font-bold mb-4">Timeline</h2>
            
            {timelineItems.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-gray-500">No {filter === 'all' ? 'events' : filter} to display</p>
                <Button 
                  variant="link" 
                  onClick={() => setFilter('all')}
                  className="mt-2"
                >
                  Show all events
                </Button>
              </div>
            ) : (
              <div className="space-y-4 relative">
                <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-gray-200" />
                
                {Object.entries(groupedItems).map(([monthYear, items]) => {
                  const [month, year] = monthYear.split('/');
                  const monthName = monthNames[parseInt(month) - 1];
                  
                  return (
                    <div key={monthYear} className="mb-6">
                      <h3 className="text-lg font-semibold mb-4 ml-10">
                        {monthName} {year}
                      </h3>
                      
                      <div className="space-y-3">
                        {Array.isArray(items) && items.map((item) => (
                          <div 
                            key={`${item.type}-${item.id}`}
                            className="flex items-start gap-4"
                          >
                            <div className="relative z-10 mt-1.5">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center 
                                ${item.type === 'issue' ? 'bg-lime/20' : 'bg-soft-blue/20'}`}>
                                {item.type === 'issue' ? (
                                  <AlertTriangle size={16} className="text-lime" />
                                ) : (
                                  <Wrench size={16} className="text-soft-blue" />
                                )}
                              </div>
                            </div>
                            
                            <div 
                              onClick={() => {
                                if (item.type === 'issue') {
                                  navigate(`/symptom-summary/${selectedCar?.id}/${item.id}`);
                                } else {
                                  navigate(`/log-repair/${selectedCar?.id}/${item.id}`);
                                }
                              }}
                              className="flex-1 p-3 border border-gray-100 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer"
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <h4 className="font-medium">{item.title}</h4>
                                  <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                                    {item.description}
                                  </p>
                                  
                                  <div className="flex justify-between items-center mt-2">
                                    <span className="text-xs text-gray-400">
                                      {format(new Date(item.date), 'MMM d, yyyy')}
                                    </span>
                                    
                                    {item.type === 'issue' && (
                                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusClass(item.status)}`}>
                                        {getStatusLabel(item.status)}
                                      </span>
                                    )}
                                    
                                    {item.type === 'repair' && item.cost !== undefined && (
                                      <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                                        ${item.cost.toFixed(2)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default ReportsPage;
